import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { catchError, map, Observable, of, shareReplay } from "rxjs";
import { HttpService } from "../http-service/http.service";
import { environment } from "../../../environments/environment";
import { API_ENDPOINTS } from "../../config/api-endpoints";
import { ApplicationContext } from "src/app/model";
import { contextToParams, rgContextToParams } from "src/app/utils";
import {
  ClientAccessInfoDto,
  ClientAccessInfoResponseDto,
} from "src/app/model/user/client-access-info.model";

@Injectable({ providedIn: "root" })
export class UserContextPermissionRoleService {
  private accessInfoCache?: {
    key: string;
    stream: Observable<ClientAccessInfoDto | null>;
  };

  constructor(private http: HttpService) {}

  getUserContextWithPermissionsAndRoles(userId: string): Observable<any> {
    const url = `${environment.apiBaseURL}${API_ENDPOINTS.user.getUserContextWithPermissionsAndRoles}`;
    const params = new HttpParams().set("userId", userId);
    return this.http.get<any>(url, params);
  }

  private getAccessInfo(
    req: ApplicationContext
  ): Observable<ClientAccessInfoResponseDto> {
    const url = `${environment.apiBaseURL}${API_ENDPOINTS.user.getAccessInfo}`;
    const params = contextToParams(req);
    return this.http.get<any>(url, params);
  }

  private getRGAccessInfo(
    req: ApplicationContext
  ): Observable<ClientAccessInfoResponseDto> {
    const url = `${environment.apiBaseURL}${API_ENDPOINTS.user.getRGAccessInfo}`;
    const params = rgContextToParams(req);
    return this.http.get<any>(url, params);
  }

  loadAccessInfo(
    req: ApplicationContext
  ): Observable<ClientAccessInfoDto | null> {
    const key = [
      req.clientId,
      req.entityId ?? '',
      req.clientTaxPeriodId,
      req.reportingGroupId ?? '',
    ].join('|');

    if (!this.accessInfoCache || this.accessInfoCache.key !== key) {
      const source$ = req.reportingGroupId
        ? this.getRGAccessInfo(req)
        : this.getAccessInfo(req);

      const stream = source$.pipe(
        map((response) => response?.Data ?? null),
        shareReplay(1),
        catchError(() => of(null))
      );

      this.accessInfoCache = { key, stream };
    }

    return this.accessInfoCache.stream;
  }

  hasAccess(info: ClientAccessInfoDto | null): {
    hasAccess: boolean;
    message: string;
    isEntityLocked: boolean;
  } {
    const isRG = !!info?.ReportingGroupName;

    const validations = [
      {
        valid: !!info,
        message: "Unable to verify your access. Please contact support.",
      },
      {
        valid: !!info?.ClientName,
        message: "Client information is missing. Please contact support.",
      },
      {
        valid: isRG ? !!info?.ReportingGroupName : !!info?.EntityName,
        message: isRG
          ? "Reporting Group information is missing. Please contact support."
          : "Entity information is missing. Please contact support.",
      },
      {
        valid: !!info?.HasAccess,
        message:
          "You do not have permission to access this workpaper. If you believe this is an error, please contact support.",
      },
    ];

    for (const check of validations) {
      if (!check.valid) {
        return {
          hasAccess: false,
          message: check.message,
          isEntityLocked: info?.IsEntityLocked ?? false,
        };
      }
      if (info?.IsEntityLocked) {
        return {
          hasAccess: true,
          message: "This workpaper context is currently locked and finalized.",
          isEntityLocked: info?.IsEntityLocked ?? false,
        };
      }
    }
    return {
      hasAccess: true,
      message: "Access granted.",
      isEntityLocked: info?.IsEntityLocked ?? false,
    };
  }
}
